import React from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowUpRight, ArrowRight, TrendingUp, Package, Boxes, Compass,
  Sparkles, FileText, Check, MessageSquare, Send,
} from "lucide-react";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";
import { CtaBand } from "../components/site/CtaBand";
import { Reveal, EASE, BOOK_DEMO_URL } from "../lib/motion";
import { ChartMotif } from "../components/site/ChartMotif";
import { useSEO, useLenis } from "../lib/hooks";
import {
  Accordion, AccordionItem, AccordionTrigger, AccordionContent,
} from "../components/ui/accordion";

const FF = () => <span className="text-[#1E3A5F]">ForesightFlow</span>;

const DASHBOARDS = [
  {
    icon: TrendingUp,
    title: "Sales Intelligence",
    alt: "ForesightFlow Sales Intelligence dashboard showing revenue trends for a fashion retailer",
    desc: "Understand revenue trends, your best and worst performing periods, and where your sales are actually coming from.",
    qs: ["Which week made the most money last quarter?", "Are weekends really carrying my sales?", "Which store or channel is quietly underperforming?"],
  },
  {
    icon: Package,
    title: "Product Intelligence",
    alt: "ForesightFlow Product Intelligence dashboard ranking best and worst performing fashion styles",
    desc: "See which products and categories are winning, which are only moving on discount, and which deserve more shelf space.",
    qs: ["Which styles make money without discounts?", "What should get more shelf space this month?", "Which category is dragging on markdowns?"],
  },
  {
    icon: Boxes,
    title: "Inventory Intelligence",
    alt: "ForesightFlow Inventory Intelligence dashboard showing deadstock risk and sell-through speed",
    desc: "Track stock health, sell-through speed and deadstock risk — before it becomes a write-off.",
    qs: ["Which sizes keep running out?", "What's about to become deadstock?", "How fast is this style actually selling?"],
  },
  {
    icon: Compass,
    title: "Market Intelligence & Growth Strategy",
    alt: "ForesightFlow Market Intelligence dashboard benchmarking a fashion retailer with AI growth direction",
    desc: "Benchmark your performance and get AI-powered direction on where the business should focus next.",
    qs: ["How do I compare to similar stores?", "Where should I invest next season?", "Which category has the most room to grow?"],
  },
];

const FAQS = [
  {
    q: "What is a business intelligence platform for fashion retail?",
    a: "A business intelligence platform for fashion retail turns the sales, product and inventory data you already collect into clear answers you can act on. ForesightFlow is built specifically for Indian fashion retailers, so instead of raw POS exports you get plain-language insight into what's selling, what's stuck and what to do next.",
  },
  {
    q: "How does ForesightFlow use my sales data?",
    a: "You connect your existing billing or POS data, and ForesightFlow's retail analytics engine reads the patterns for you — revenue trends, product performance, sell-through speed and deadstock risk. Nothing is changed in your system; we simply make your own numbers readable.",
  },
  {
    q: "Do I need technical skills to use ForesightFlow?",
    a: "No. There are no spreadsheets or formulas to build. You read plain-language dashboards, and when you have a question you just ask the AI Assistant in normal words. ForesightFlow is designed for busy store owners, not data teams.",
  },
  {
    q: "How is ForesightFlow different from a regular POS report?",
    a: "A POS report tells you what happened. ForesightFlow, as a retail business intelligence platform, tells you what it means and what to do — connecting sales, product and inventory intelligence so you can see margin health, deadstock risk and growth opportunities in one place.",
  },
  {
    q: "How much does ForesightFlow cost?",
    a: "ForesightFlow starts at ₹15,000 per month for the Essential plan (Sales, Product and Inventory Intelligence with 100 AI queries), and ₹20,000 per month for the Intelligence plan, which adds Market Intelligence, Growth Strategy and unlimited AI queries. Customisations are available at ₹1,000 each.",
  },
  {
    q: "Is my retail data secure with ForesightFlow?",
    a: "Yes. Your data is used only to power your own dashboards and AI answers. We never share or resell it, and access stays with your business and the JSC team supporting you.",
  },
];

const Panel = ({ ariaLabel, children }) => (
  <div
    role="img"
    aria-label={ariaLabel}
    className="relative rounded-[4px] border border-[rgba(28,28,30,0.12)] bg-[#FAF8F5] shadow-[0_30px_80px_-50px_rgba(28,28,30,0.5)] overflow-hidden"
  >
    <div className="flex items-center gap-1.5 px-4 py-3 bg-[#1C1C1E]">
      <span className="w-2.5 h-2.5 rounded-full bg-[#B39B77]" />
      <span className="w-2.5 h-2.5 rounded-full bg-[#3E4A5C]" />
      <span className="w-2.5 h-2.5 rounded-full bg-[#3E4A5C]/60" />
      <span className="ml-3 font-body text-[10px] uppercase tracking-[0.2em] text-[#FAF8F5]/70">ForesightFlow</span>
    </div>
    <div className="p-6">{children}</div>
  </div>
);

const SchemaMarkup = () => {
  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQS.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };
  const appSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "ForesightFlow",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    description:
      "Business intelligence platform for Indian fashion retailers, by Jay Shah Consultancy. Sales, product and inventory intelligence with an AI assistant.",
    offers: [
      { "@type": "Offer", name: "Essential Plan", price: "15000", priceCurrency: "INR" },
      { "@type": "Offer", name: "Intelligence Plan", price: "20000", priceCurrency: "INR" },
    ],
    publisher: { "@type": "Organization", name: "Jay Shah Consultancy", address: "Pune, India" },
  };
  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(appSchema) }} />
    </>
  );
};

const ForesightFlow = () => {
  useLenis();
  useSEO({
    title: "ForesightFlow — Business Intelligence Platform for Indian Fashion Retailers, by JSC",
    description:
      "ForesightFlow is business intelligence software for fashion retail — a retail analytics platform in India with sales, product and inventory intelligence for retailers.",
    keywords:
      "ForesightFlow, business intelligence platform India, fashion retail analytics software, retail inventory intelligence, sales intelligence dashboard for retail, AI assistant for retail business, retail business intelligence Pune, fashion retail data platform",
  });

  return (
    <div className="jsc-grain relative bg-[#FAF8F5] min-h-screen" data-testid="page-foresightflow">
      <SchemaMarkup />
      <Navbar />

      {/* 2. HERO */}
      <section className="relative pt-36 md:pt-44 lg:pt-48 pb-16 md:pb-24 overflow-hidden">
        <div className="jsc-gridlines absolute inset-0 opacity-30 [mask-image:radial-gradient(ellipse_at_top_right,black,transparent_70%)]" />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6">
              <Reveal>
                <motion.div
                  initial={{ opacity: 0, y: 16, scale: 0.96 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  transition={{ duration: 0.8, ease: EASE }}
                  data-testid="ff-logo"
                  className="inline-flex items-center rounded-2xl overflow-hidden bg-[#0E1A2B] p-2.5 mb-8 shadow-[0_24px_60px_-28px_rgba(30,58,95,0.7)] ring-1 ring-[#1E3A5F]/40"
                >
                  <img
                    src="/assets/foresightflow-logo.png"
                    alt="ForesightFlow logo — business intelligence platform for fashion retail"
                    width="240" height="64" loading="eager"
                    className="h-14 md:h-16 w-auto object-contain rounded-xl"
                  />
                </motion.div>
                <p className="font-body text-xs md:text-sm uppercase tracking-[0.24em] text-[#1C1C1E] mb-7 flex items-center gap-3">
                  <span className="inline-block w-8 h-px bg-[#B39B77]" />
                  The Platform
                </p>
              </Reveal>
              <h1 data-testid="ff-headline" className="font-display font-semibold text-[#1C1C1E] text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight">
                Just ask your business.
              </h1>
              <Reveal delay={0.12}>
                <p className="mt-8 max-w-xl font-body text-lg md:text-xl leading-relaxed text-[#3E4A5C]">
                  <FF /> turns your own sales, product and inventory data into instant, clear
                  answers — no spreadsheets required. Business intelligence software built for
                  Indian fashion retail.
                </p>
              </Reveal>
              <Reveal delay={0.2}>
                <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
                  <a href={BOOK_DEMO_URL} target="_blank" rel="noopener noreferrer" data-testid="ff-hero-book-demo"
                    className="group inline-flex items-center gap-2 rounded-full bg-[#B39B77] px-7 py-4 font-body text-base font-medium text-[#1C1C1E] transition-colors duration-300 hover:bg-[#96805E]">
                    Book a Demo
                    <ArrowUpRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </a>
                  <a href="#pricing" data-testid="ff-hero-pricing"
                    className="group inline-flex items-center gap-2 rounded-full border border-[#1C1C1E]/25 px-7 py-4 font-body text-base font-medium text-[#1C1C1E] transition-colors duration-300 hover:border-[#1C1C1E]">
                    See Pricing
                    <ArrowRight className="w-5 h-5 transition-transform duration-300 group-hover:translate-x-1" />
                  </a>
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-6">
              <Reveal delay={0.15}>
                <Panel ariaLabel="ForesightFlow Sales Intelligence dashboard mockup showing weekly revenue and top styles for a fashion retailer">
                  <img
                    src="/assets/foresightflow-logo.png"
                    alt="ForesightFlow logo"
                    width="150" height="40" loading="lazy"
                    className="h-9 w-auto object-contain rounded-sm mb-5"
                  />
                  <div className="flex items-center justify-between mb-1">
                    <span className="font-body text-[10px] uppercase tracking-[0.22em] text-[#3E4A5C]">Weekly revenue</span>
                    <span className="font-body text-xs font-semibold text-[#B39B77]">+18.4%</span>
                  </div>
                  <p className="font-display text-3xl text-[#1C1C1E] mb-4">&#8377; 4.82L</p>
                  <ChartMotif className="w-full h-auto" />
                  <div className="mt-6 grid grid-cols-3 gap-3 border-t border-[rgba(28,28,30,0.1)] pt-5">
                    {[["Best day", "Saturday"], ["Top style", "Linen co-ord"], ["Restock", "3 SKUs"]].map(([k, v]) => (
                      <div key={k}>
                        <p className="font-body text-[9px] uppercase tracking-[0.14em] text-[#3E4A5C]/70">{k}</p>
                        <p className="font-body text-sm font-medium text-[#1C1C1E] mt-1">{v}</p>
                      </div>
                    ))}
                  </div>
                </Panel>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* 3. PROBLEM RESTATED */}
      <section className="relative py-20 md:py-28 border-t border-[rgba(28,28,30,0.08)]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-4">
              <Reveal><p className="font-body text-xs uppercase tracking-[0.24em] text-[#B39B77]">The gap we close</p></Reveal>
            </div>
            <div className="lg:col-span-8">
              <Reveal delay={0.1}>
                <h2 className="font-display font-semibold text-[#1C1C1E] text-3xl md:text-5xl leading-[1.05] tracking-tight">
                  You already track sales. You still can&apos;t get answers fast.
                </h2>
              </Reveal>
              <Reveal delay={0.15}>
                <p className="mt-8 max-w-2xl font-body text-lg leading-relaxed text-[#3E4A5C]">
                  Most fashion retailers record every sale in some system — but ask a simple
                  question like &ldquo;which products are actually making money?&rdquo; or &ldquo;which
                  sizes keep running out?&rdquo; and the answer takes hours of digging, if it comes
                  at all. That exact gap between having data and getting answers is what <FF /> closes.
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* 4. FOUR CORE DASHBOARDS */}
      <section id="platform" className="relative py-20 md:py-28 bg-[#F0EBE1]/40 border-y border-[rgba(28,28,30,0.06)] scroll-mt-24">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
          <Reveal>
            <p className="font-body text-xs uppercase tracking-[0.24em] text-[#B39B77] mb-4">The platform</p>
            <h2 className="font-display font-semibold text-[#1C1C1E] text-3xl md:text-5xl tracking-tight max-w-2xl">
              Four dashboards. One clear view of your store.
            </h2>
          </Reveal>

          <div className="mt-16 space-y-16 md:space-y-24">
            {DASHBOARDS.map((d, i) => {
              const Icon = d.icon;
              const flip = i % 2 === 1;
              return (
                <Reveal key={d.title} delay={0.05}>
                  <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center" data-testid={`dashboard-${i}`}>
                    <div className={`lg:col-span-6 ${flip ? "lg:order-2" : ""}`}>
                      <Panel ariaLabel={d.alt}>
                        <div className="flex items-center justify-between mb-5">
                          <Icon className="w-7 h-7 text-[#3E4A5C]" strokeWidth={1.4} />
                          <span className="font-body text-[10px] uppercase tracking-[0.2em] text-[#3E4A5C]">{d.title}</span>
                        </div>
                        <div className="flex items-end gap-2 h-28">
                          {[42, 68, 55, 80, 60, 92, 74].map((h, bi) => (
                            <div key={bi} className="flex-1 rounded-sm" style={{ height: `${h}%`, background: bi === 5 ? "#B39B77" : "rgba(62,74,92,0.28)" }} />
                          ))}
                        </div>
                        <div className="mt-5 grid grid-cols-3 gap-3 border-t border-[rgba(28,28,30,0.1)] pt-4">
                          {["This week", "vs target", "Trend"].map((k, ki) => (
                            <div key={k}>
                              <p className="font-body text-[9px] uppercase tracking-[0.14em] text-[#3E4A5C]/70">{k}</p>
                              <p className="font-body text-sm font-medium text-[#1C1C1E] mt-1">{["Strong", "+6%", "Up"][ki]}</p>
                            </div>
                          ))}
                        </div>
                      </Panel>
                    </div>
                    <div className={`lg:col-span-6 ${flip ? "lg:order-1" : ""}`}>
                      <h3 className="font-display text-2xl md:text-3xl text-[#1C1C1E]">{d.title}</h3>
                      <p className="mt-4 font-body text-lg leading-relaxed text-[#3E4A5C] max-w-lg">{d.desc}</p>
                      <ul className="mt-6 space-y-3">
                        {d.qs.map((q) => (
                          <li key={q} className="flex items-start gap-3 font-body text-base text-[#1C1C1E]">
                            <MessageSquare className="w-4 h-4 mt-1 shrink-0 text-[#B39B77]" strokeWidth={1.6} />
                            <span className="italic">&ldquo;{q}&rdquo;</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* 5. AI ASSISTANT */}
      <section className="relative py-20 md:py-28">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5">
              <Reveal>
                <p className="font-body text-xs uppercase tracking-[0.24em] text-[#B39B77] mb-4 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" /> AI Assistant
                </p>
                <h2 className="font-display font-semibold text-[#1C1C1E] text-3xl md:text-5xl leading-[1.05] tracking-tight">
                  Just ask your business.
                </h2>
                <p className="mt-6 font-body text-lg leading-relaxed text-[#3E4A5C] max-w-md">
                  Type a question the way you&apos;d ask a smart friend. The <FF /> AI assistant for
                  your retail business reads your data and answers in plain language — instantly.
                </p>
                <div className="mt-6 space-y-2">
                  {["Which products had the highest return rate last month?", "What should I restock before the festive season?"].map((p) => (
                    <p key={p} className="font-body text-sm text-[#3E4A5C] italic">&ldquo;{p}&rdquo;</p>
                  ))}
                </div>
              </Reveal>
            </div>

            <div className="lg:col-span-7">
              <Reveal delay={0.15}>
                <div role="img" aria-label="ForesightFlow AI assistant chat answering a fashion retailer's question in plain language"
                  className="rounded-[4px] border border-[rgba(28,28,30,0.12)] bg-[#FAF8F5] shadow-[0_30px_80px_-50px_rgba(28,28,30,0.5)] p-6 md:p-8" data-testid="ai-assistant-mock">
                  <div className="flex justify-end mb-4">
                    <div className="max-w-[80%] rounded-2xl rounded-tr-sm bg-[#1C1C1E] text-[#FAF8F5] px-5 py-3 font-body text-sm">
                      What should I restock before the festive season?
                    </div>
                  </div>
                  <div className="flex justify-start">
                    <div className="max-w-[85%] rounded-2xl rounded-tl-sm bg-[#F0EBE1] px-5 py-4 font-body text-sm text-[#1C1C1E] leading-relaxed">
                      Based on last year&apos;s festive sell-through and current velocity, prioritise
                      <span className="font-semibold text-[#1E3A5F]"> ethnic co-ords (M, L)</span> and
                      <span className="font-semibold text-[#1E3A5F]"> kurta sets</span>. Hold back on
                      denim — it slowed 22% this month. Estimated stockout risk on 3 SKUs within 9 days.
                    </div>
                  </div>
                  <div className="mt-6 flex items-center gap-2 rounded-full border border-[rgba(28,28,30,0.15)] px-4 py-2.5">
                    <input disabled placeholder="Ask your business anything…" className="flex-1 bg-transparent font-body text-sm text-[#3E4A5C] outline-none" />
                    <span className="w-8 h-8 rounded-full bg-[#B39B77] flex items-center justify-center"><Send className="w-4 h-4 text-[#1C1C1E]" /></span>
                  </div>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* 6. AI REPORT */}
      <section className="relative py-20 md:py-28 bg-[#F0EBE1]/40 border-y border-[rgba(28,28,30,0.06)]">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 lg:order-2">
              <Reveal>
                <p className="font-body text-xs uppercase tracking-[0.24em] text-[#B39B77] mb-4 flex items-center gap-2">
                  <FileText className="w-4 h-4" /> AI Report
                </p>
                <h2 className="font-display font-semibold text-[#1C1C1E] text-3xl md:text-5xl leading-[1.05] tracking-tight">
                  Your whole business, summarised in minutes.
                </h2>
                <p className="mt-6 font-body text-lg leading-relaxed text-[#3E4A5C] max-w-lg">
                  Instead of digging through raw numbers, read a plain-language summary that <FF />
                  {" "}writes for you — what changed, what it means, and what to do next.
                </p>
              </Reveal>
            </div>
            <div className="lg:col-span-6 lg:order-1">
              <Reveal delay={0.15}>
                <div role="img" aria-label="ForesightFlow AI report preview summarising a fashion retailer's monthly performance in plain language"
                  className="rounded-[4px] border border-[rgba(28,28,30,0.12)] bg-[#FAF8F5] shadow-[0_30px_80px_-50px_rgba(28,28,30,0.5)] p-8" data-testid="ai-report-mock">
                  <p className="font-body text-[10px] uppercase tracking-[0.22em] text-[#B39B77]">Monthly report · November</p>
                  <h3 className="mt-2 font-display text-2xl text-[#1C1C1E]">The month in one read</h3>
                  <div className="mt-5 space-y-4">
                    {[
                      "Revenue up 18.4% MoM, led by ethnic wear and co-ord sets.",
                      "Margins held steady — discount dependency fell to 21%.",
                      "3 SKUs at stockout risk; 2 slow movers flagged as deadstock.",
                    ].map((line) => (
                      <div key={line} className="flex items-start gap-3">
                        <Check className="w-4 h-4 mt-1 shrink-0 text-[#B39B77]" />
                        <p className="font-body text-sm leading-relaxed text-[#3E4A5C]">{line}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-6 h-px bg-[rgba(28,28,30,0.1)]" />
                  <p className="mt-4 font-body text-sm italic text-[#1C1C1E]">Recommended focus: restock festive ethnic lines; clear denim before December.</p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* 7. CAMPAIGN BAND */}
      <section className="relative bg-[#1C1C1E] py-24 md:py-36 overflow-hidden" data-testid="ff-campaign-band">
        <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: "linear-gradient(to right,#B39B77 1px,transparent 1px),linear-gradient(to bottom,#B39B77 1px,transparent 1px)", backgroundSize: "72px 72px" }} />
        <div className="relative max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 text-center">
          <h2 className="font-display font-semibold text-[#B39B77] text-3xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-4xl mx-auto">
            It&apos;s like Googling your own business.
          </h2>
        </div>
      </section>

      {/* 8. PRICING */}
      <section id="pricing" className="relative py-20 md:py-28 scroll-mt-24">
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
          <Reveal>
            <p className="font-body text-xs uppercase tracking-[0.24em] text-[#B39B77] mb-4">Pricing</p>
            <h2 className="font-display font-semibold text-[#1C1C1E] text-3xl md:text-5xl tracking-tight max-w-2xl">
              Simple, transparent plans.
            </h2>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {/* Essential */}
            <Reveal>
              <div data-testid="plan-essential" className="h-full flex flex-col rounded-[4px] border border-[rgba(28,28,30,0.14)] bg-[#FAF8F5] p-8 md:p-10">
                <p className="font-body text-sm uppercase tracking-[0.18em] text-[#3E4A5C]">Essential Plan</p>
                <p className="mt-5 font-display text-5xl text-[#1C1C1E]">&#8377;15,000<span className="font-body text-base text-[#3E4A5C]">/month</span></p>
                <ul className="mt-8 space-y-4 flex-1">
                  {["Sales Intelligence", "Product Intelligence", "Inventory Intelligence", "100 AI queries per month"].map((f) => (
                    <li key={f} className="flex items-start gap-3 font-body text-base text-[#1C1C1E]"><Check className="w-5 h-5 mt-0.5 shrink-0 text-[#B39B77]" />{f}</li>
                  ))}
                </ul>
                <a href={BOOK_DEMO_URL} target="_blank" rel="noopener noreferrer" data-testid="plan-essential-cta"
                  className="mt-10 inline-flex items-center justify-center gap-2 rounded-full border border-[#1C1C1E]/25 px-7 py-3.5 font-body text-base font-medium text-[#1C1C1E] transition-colors duration-300 hover:border-[#1C1C1E]">
                  Book a Demo
                </a>
              </div>
            </Reveal>

            {/* Intelligence (featured) */}
            <Reveal delay={0.1}>
              <div data-testid="plan-intelligence" className="relative h-full flex flex-col rounded-[4px] border border-[#1E3A5F] bg-[#1C1C1E] p-8 md:p-10">
                <span className="absolute top-6 right-6 rounded-full bg-[#B39B77] px-3 py-1 font-body text-[10px] uppercase tracking-[0.16em] text-[#1C1C1E]">Most complete</span>
                <p className="font-body text-sm uppercase tracking-[0.18em] text-[#B39B77]">Intelligence Plan</p>
                <p className="mt-5 font-display text-5xl text-[#FAF8F5]">&#8377;20,000<span className="font-body text-base text-[#FAF8F5]/60">/month</span></p>
                <ul className="mt-8 space-y-4 flex-1">
                  {["Everything in Essential", "Market Intelligence", "Growth Strategy", "Unlimited AI queries", "Bi-monthly data refresh"].map((f) => (
                    <li key={f} className="flex items-start gap-3 font-body text-base text-[#FAF8F5]"><Check className="w-5 h-5 mt-0.5 shrink-0 text-[#B39B77]" />{f}</li>
                  ))}
                </ul>
                <a href={BOOK_DEMO_URL} target="_blank" rel="noopener noreferrer" data-testid="plan-intelligence-cta"
                  className="mt-10 inline-flex items-center justify-center gap-2 rounded-full bg-[#B39B77] px-7 py-3.5 font-body text-base font-medium text-[#1C1C1E] transition-colors duration-300 hover:bg-[#96805E]">
                  Book a Demo <ArrowUpRight className="w-5 h-5" />
                </a>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.15}>
            <div data-testid="custom-pricing" className="mt-10 rounded-[4px] border border-[rgba(28,28,30,0.14)] bg-[#F0EBE1]/50 p-8 md:p-10 grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-8">
                <p className="font-body text-xs uppercase tracking-[0.2em] text-[#B39B77] mb-3">Tailored to your store</p>
                <h3 className="font-display text-2xl md:text-3xl text-[#1C1C1E] leading-snug">
                  Final pricing is customised to the features and plan you choose.
                </h3>
                <p className="mt-4 font-body text-base leading-relaxed text-[#3E4A5C] max-w-2xl">
                  Every store is different, so we tailor ForesightFlow to what you actually need.
                  Add-on customisations start at &#8377;1,000 each, and human-in-the-loop consulting
                  is available as a separate engagement. Tell us your setup and we&apos;ll put together
                  the right plan for you.
                </p>
              </div>
              <div className="lg:col-span-4 flex flex-col sm:flex-row lg:flex-col gap-3">
                <a
                  href={BOOK_DEMO_URL} target="_blank" rel="noopener noreferrer" data-testid="custom-pricing-demo"
                  className="group inline-flex items-center justify-center gap-2 rounded-full bg-[#B39B77] px-7 py-3.5 font-body text-base font-medium text-[#1C1C1E] transition-colors duration-300 hover:bg-[#96805E]"
                >
                  Book a Demo <ArrowUpRight className="w-5 h-5" />
                </a>
                <a
                  href="#platform" data-testid="custom-pricing-explore"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-[#1C1C1E]/25 px-7 py-3.5 font-body text-base font-medium text-[#1C1C1E] transition-colors duration-300 hover:border-[#1C1C1E]"
                >
                  Explore the product <ArrowRight className="w-5 h-5" />
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 9. FAQ */}
      <section className="relative py-20 md:py-28 bg-[#F0EBE1]/40 border-y border-[rgba(28,28,30,0.06)]">
        <div className="max-w-[900px] mx-auto px-6 md:px-12">
          <Reveal>
            <p className="font-body text-xs uppercase tracking-[0.24em] text-[#B39B77] mb-4">FAQ</p>
            <h2 className="font-display font-semibold text-[#1C1C1E] text-3xl md:text-5xl tracking-tight">
              Questions retail owners actually ask.
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="mt-12" data-testid="faq-accordion">
              {FAQS.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-[rgba(28,28,30,0.14)]">
                  <AccordionTrigger className="py-6 font-display text-lg md:text-xl text-[#1C1C1E] hover:no-underline" data-testid={`faq-trigger-${i}`}>
                    <h3 className="font-display text-lg md:text-xl text-left">{f.q}</h3>
                  </AccordionTrigger>
                  <AccordionContent className="font-body text-base leading-relaxed text-[#3E4A5C] pb-6 pr-8">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>

          <Reveal delay={0.15}>
            <p className="mt-10 font-body text-base text-[#3E4A5C]">
              Curious about the people building it?{" "}
              <Link to="/about" className="text-[#1C1C1E] link-underline font-medium">Learn about the team behind ForesightFlow</Link>, or read the{" "}
              <Link to="/blogs" className="text-[#1C1C1E] link-underline font-medium">JSC fashion retail insights blog</Link>.
            </p>
          </Reveal>
        </div>
      </section>

      {/* 10. CLOSING CTA */}
      <CtaBand eyebrow="Ready when you are" title="See what your business has been trying to tell you." testid="ff-cta" />

      <Footer />
    </div>
  );
};

export default ForesightFlow;
